// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonIcon, ButtonProps } from "@insite/mobius/Button/Props";
import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import safeColor from "@insite/mobius/utilities/safeColor";
import styleObjectToString from "@insite/mobius/utilities/styleObjectToString";
import { buttonColors } from "@vanmeter/XDL/PreStyle/button";
import { getSystemColor } from "@vanmeter/XDL/Utilities";
import { css, ThemeProps } from "styled-components";

type ButtonTypesProps = ThemeProps<BaseTheme> &
    Pick<ButtonProps, "hoverMode" | "hoverStyle" | "activeMode" | "activeStyle"> & {
        _color?: string;
    };

const calculateHoverColor = ({ _color, hoverMode = "darken", hoverStyle, theme = baseTheme }: ButtonTypesProps) => {
    if (hoverMode === null || hoverMode === "none") {
        return _color;
    }
    if (hoverStyle?.color) {
        return resolveColor(hoverStyle.color, theme);
    }
    return safeColor(resolveColor(_color, theme))[hoverMode](0.3).string();
};

const calculateActiveColor = ({ _color, activeMode = "darken", activeStyle, theme = baseTheme }: ButtonTypesProps) => {
    if (activeMode === null || activeMode === "none") {
        return _color;
    }
    if (activeStyle?.color) {
        return resolveColor(activeStyle.color, theme);
    }
    return safeColor(resolveColor(_color, theme))[activeMode](0.6).string();
};

const buttonTypes = {
    outline: (props: ButtonTypesProps) => {
        const mainColor = resolveColor(props._color, props.theme);
        const disabledColor = resolveColor("common.disabled", props.theme);
        const hoverColor = calculateHoverColor(props);
        const activeColor = calculateActiveColor(props);
        return css`
            border: 1px solid ${mainColor};
            background: transparent;
            color: ${mainColor};
            ${ButtonIcon} {
                color: ${mainColor};
            }
            &:not(:disabled):hover {
                color: ${hoverColor};
                border-color: ${hoverColor};
                ${styleObjectToString(props.hoverStyle)};
                ${ButtonIcon} {
                    color: ${hoverColor};
                }
            }
            // XNG-Changes: add focus
            &:not(:disabled):active,
            &:not(:disabled):focus {
                color: ${activeColor};
                border-color: ${activeColor};
                ${styleObjectToString(props.activeStyle)};
                ${ButtonIcon} {
                    color: ${activeColor};
                }
            }
            &:disabled {
                // XNG-Changes: add opacity
                opacity: 0.33;
            }
        `;
    },
    solid: (props: ButtonTypesProps) => {
        const mainColor = resolveColor(props._color, props.theme);
        const hoverColor = calculateHoverColor(props);
        const activeColor = calculateActiveColor(props);
        // XNG-Changes: use maincolor
        const disabledColor = getSystemColor(mainColor, 0.33);
        // const contrastColor = getContrastColor(props._color, props.theme);

        // XNG-Changes
        const isSecondary = props._color === buttonColors.secondary.main;
        const textColor = isSecondary ? getSystemColor("primary") : getSystemColor("common.white");
        const disabledTextColor = isSecondary ? getSystemColor("primary", 0.33) : getSystemColor("common.white");

        return css`
            background: ${mainColor};
            border: 2px solid ${mainColor};
            color: ${textColor};
            ${ButtonIcon} {
                color: ${textColor};
            }
            &:hover {
                background: ${hoverColor};
                border-color: ${hoverColor};
                ${styleObjectToString(props.hoverStyle)};
            }
            // XNG-Changes: add focus
            &:active,
            &:focus {
                background: ${activeColor};
                border-color: ${activeColor};
                ${styleObjectToString(props.activeStyle)};
            }
            &:disabled {
                background: ${disabledColor};
                // XNG-Changes
                border-color: transparent;
                color: ${disabledTextColor};
                ${ButtonIcon} {
                    opacity: 0.33;
                }
            }
        `;
    },
};

export default buttonTypes;
